import React from 'react';

import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import { PlayPauseButton } from '../../../controls/PlayPauseButton';
import { PlayFromBeginningButton } from '../../../controls/PlayFromBeginningButton';
import { SkipBackButton } from '../../../controls/SkipBackButton';
import { SkipForwardButton } from '../../../controls/SkipForwardButton';
import { PlaybackSpeedControl } from '../../../controls/PlaybackSpeedControl';
import { AudioTimeIndicator } from '../../../controls/AudioTimeIndicator';
import { getSharedAttributes } from '../../../../utils/getSharedAttributes';

type AudioBriefingPopupListenPanelProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	handleFetchContent: () => void;
};

const styles = cssMap({
	wrapper: {
		flex: 1,
	},
	outerControlButtonWrapper: {
		// This is the width of the speed option that requires the most space
		// Ensures that the displayed speed is never truncated and that spacing remains uniform
		width: '54px',
	},
});

export const AudioBriefingPopupListenPanel = ({
	audioRef,
	handleFetchContent,
}: AudioBriefingPopupListenPanelProps) => {
	const [
		{ activeFeature, isPlaying, isLoading, hasFetched, playbackSpeed, lastGeneratedBriefingConfig },
	] = useAudioBriefing();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const shouldDisableControls = isLoading || !hasFetched;

	const sharedAttributes = getSharedAttributes({
		customization: lastGeneratedBriefingConfig?.customization,
		additionalResources: lastGeneratedBriefingConfig?.additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	});

	const handlePlayFromBeginningButtonClick = () => {
		trackAIResultAction('audioPlayFromBeginning', {
			attributes: sharedAttributes,
		});
	};

	const handlePlayPauseButtonClick = () => {
		if (isPlaying) {
			trackAIResultAction('audioPause', {
				attributes: sharedAttributes,
			});
		} else {
			trackAIResultAction('audioPlay', {
				attributes: sharedAttributes,
			});
		}
	};

	const handleSkipBackButtonClick = () => {
		trackAIResultAction('audioSkipBackward', {
			attributes: sharedAttributes,
		});
	};

	const handleSkipForwardButtonClick = () => {
		trackAIResultAction('audioSkipForward', {
			attributes: sharedAttributes,
		});
	};

	const handlePlaybackSpeedButtonClick = (speed: number) => {
		trackAIResultAction('audioPlaybackSpeed', {
			attributes: { ...sharedAttributes, playbackSpeed: speed },
		});
	};

	return (
		<Box paddingBlock="space.100" xcss={styles.wrapper} testId="listen-panel-controls">
			<Stack space="space.150" alignInline="center">
				<Inline space="space.150" alignBlock="center">
					<Flex xcss={styles.outerControlButtonWrapper} justifyContent="center">
						<PlayFromBeginningButton
							audioRef={audioRef}
							isDisabled={shouldDisableControls}
							onClick={handlePlayFromBeginningButtonClick}
							onRestart={handleFetchContent}
						/>
					</Flex>

					<SkipBackButton
						audioRef={audioRef}
						isDisabled={shouldDisableControls}
						onClick={handleSkipBackButtonClick}
					/>
					<PlayPauseButton
						audioRef={audioRef}
						isPlaying={isPlaying}
						isLoading={isLoading}
						hasFetched={hasFetched}
						fetchContent={handleFetchContent}
						onClick={handlePlayPauseButtonClick}
					/>
					<SkipForwardButton
						audioRef={audioRef}
						isDisabled={shouldDisableControls}
						onClick={handleSkipForwardButtonClick}
					/>

					<Box xcss={styles.outerControlButtonWrapper}>
						<PlaybackSpeedControl
							audioRef={audioRef}
							isDisabled={shouldDisableControls}
							onClick={handlePlaybackSpeedButtonClick}
						/>
					</Box>
				</Inline>
				<AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} hasFetched={hasFetched} />
			</Stack>
		</Box>
	);
};
